<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxButton,
  NxErrorSummary,
  NxLabel,
  NxLoadingWrapper,
  NxSidePanel,
  NxTextInput,
  NxToggleGroup,
} from '@blpde/nx-ui';
import type { DictionaryLanguage } from '../dictionaryFile';
import { dictionaryWriteFailure, type DictionaryWriteResult } from './level';
import { DICTIONARY_TEXT } from './labels';
import type { CorrectionDraft, DraftProblem } from './drafts';
import { useDictionaryEditorWrite } from './editorSave';
import ExplainPanel from './ExplainPanel.vue';

/**
 * The side panel of one correction (I-3024): the wrong word, what the search
 * suggests instead and the language. Saving checks the draft first (the word
 * rule, a twin in the layer) and names the field; master's own refusal shows
 * in the panel's error summary, the draft left as typed.
 */
const props = defineProps<{
  /** The entry being edited, or nothing for a new one. */
  editing: { lang: DictionaryLanguage; typed: string } | null;
  typed: string;
  to: string;
  lang: DictionaryLanguage;
  /** Whose layer is written, as the subtitle names it. */
  levelLabel: string;
  /** The draft's problem, if any. */
  problem: (draft: CorrectionDraft) => DraftProblem | null;
  save: (draft: CorrectionDraft) => Promise<DictionaryWriteResult>;
}>();

const emit = defineEmits<{ close: []; reload: [] }>();

const { t } = useI18n();
const T = `${DICTIONARY_TEXT}.editor`;
const typed = ref(props.typed);
const to = ref(props.to);
const lang = ref<DictionaryLanguage>(props.lang);
const errorSummary = ref<{ focus: () => void } | null>(null);
const fieldLabel: Record<string, string> = {
  typed: t(`${T}.typed`),
  to: t(`${T}.target`),
  lang: t(`${T}.language`),
};
const { entries: summaryEntries, onReload, onCopyFailed, focusRequests, fieldErrors, locked, saving, run } =
  useDictionaryEditorWrite({
    kind: () => (props.editing ? 'update' : 'create'),
    fields: ['typed', 'to', 'lang'],
    labelFor: (field) => (field.key ? fieldLabel[field.key] : undefined),
    reload: () => emit('reload'),
  });

watch(typed, () => fieldErrors.clear('typed'));
watch(to, () => fieldErrors.clear('to'));
watch(lang, () => {
  fieldErrors.clear('lang');
  fieldErrors.clear('typed');
});
watch(focusRequests, () => errorSummary.value?.focus());

function requestClose(): void {
  if (locked.value) return;
  emit('close');
}

function submit(): void {
  if (locked.value) return;
  const draft = { typed: typed.value, to: to.value, lang: lang.value };
  const problem = props.problem(draft);
  if (problem) {
    fieldErrors.show(new Map([[problem.field, t(`${T}.problems.${problem.code}`, problem.params)]]));
    return;
  }
  void run(async () => {
    const answer = await props.save(draft);
    if (!answer.ok) throw dictionaryWriteFailure(answer);
    emit('close');
  });
}

const languages = [
  { value: 'de' as const, label: t(`${DICTIONARY_TEXT}.langs.de`) },
  { value: 'en' as const, label: t(`${DICTIONARY_TEXT}.langs.en`) },
];
</script>

<template>
  <NxSidePanel
    :title="t(editing ? `${T}.correctionTitle` : `${T}.correctionCreateTitle`)"
    :subtitle="levelLabel"
    width="lg"
    :close-label="t(`${DICTIONARY_TEXT}.close`)"
    @close="requestClose"
  >
    <div class="dict-side">
      <NxErrorSummary
        ref="errorSummary"
        data-testid="dictionary-editor-error"
        :entries="summaryEntries"
        @reload="onReload"
        @copy-failed="onCopyFailed"
      />
      <NxLabel :text="t(`${T}.typed`)">
        <NxLoadingWrapper
          data-testid="dictionary-editor-typed"
          :loading="false"
          :error="!!fieldErrors.errorFor('typed')"
          :error-message="fieldErrors.errorFor('typed')"
        >
          <NxTextInput v-model="typed" :disabled="locked" />
        </NxLoadingWrapper>
      </NxLabel>
      <NxLabel :text="t(`${T}.target`)">
        <NxLoadingWrapper
          data-testid="dictionary-editor-target"
          :loading="false"
          :error="!!fieldErrors.errorFor('to')"
          :error-message="fieldErrors.errorFor('to')"
        >
          <NxTextInput v-model="to" :disabled="locked" />
        </NxLoadingWrapper>
      </NxLabel>
      <NxLabel :text="t(`${T}.language`)">
        <NxToggleGroup v-model="lang" :label="t(`${DICTIONARY_TEXT}.langs.label`)" :options="languages" :disabled="locked" />
      </NxLabel>
      <ExplainPanel :initial-query="props.typed" />
    </div>
    <template #footer>
      <div class="dict-side-foot">
        <NxButton variant="outline" :disabled="locked" @click="requestClose">{{ t(`${T}.cancel`) }}</NxButton>
        <NxButton data-testid="dictionary-save" :disabled="locked" :loading="saving" @click="submit">
          {{ t(`${T}.save`) }}
        </NxButton>
      </div>
    </template>
  </NxSidePanel>
</template>

<style scoped>
.dict-side {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
}
.dict-side-foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
}
</style>
